<script setup lang="ts">
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~ds/components/ui/select';
import { Heading } from '~ds/components/ui/typography';
import { useI18n } from 'vue-i18n';

import type { FeedbackSurveyQuestionDropdown } from './types';

import { feedbackOptionLabel, feedbackQuestionLabel } from './types';

const props = defineProps<{
  question: FeedbackSurveyQuestionDropdown;
}>();

const model = defineModel<string | number | null | undefined>();
const { t } = useI18n();

const label = computed(() => feedbackQuestionLabel(props.question, t));

const options = computed(() => props.question.options.map(option => ({
  value: option.value,
  label: feedbackOptionLabel(option, t),
})));

// The visible heading is the question, so the trigger borrows it as its accessible name rather
// than repeating it in a second, hidden label.
const headingId = useId();
</script>

<template>
  <div class="flex flex-col items-center gap-6">
    <Heading
      :id="headingId"
      size="md"
      as="h3"
      class="text-center max-w-sm"
    >
      {{ label }}
    </Heading>
    <div class="w-full max-w-sm">
      <Select
        :model-value="model == null ? undefined : String(model)"
        @update:model-value="(value) => (model = value == null ? undefined : String(value))"
      >
        <SelectTrigger
          class="w-full"
          :aria-labelledby="headingId"
        >
          <SelectValue :placeholder="$t('unified.common.select')" />
        </SelectTrigger>
        <!-- The list is portalled to <body>, so it is stacked against whatever surface the survey
             is being answered on rather than against the question: the DS overlay layer is z-50,
             the activity credenza z-60 and KUICoreDialog z-100, any of which would hide the list. -->
        <SelectContent class="z-101">
          <SelectItem
            v-for="option in options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
</template>
